import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Form, Button, Table, Collapse } from 'react-bootstrap-v5';

export default function Coaching() {
  const { id } = useParams();

  const [coachingData, setCoachingData] = useState({
    start_date: '',
    session_number: '1',
    action_recommended: '',
    venue: '',
    discussion: ''
  });

  const [sessions, setSessions] = useState([]);
  const [status, setStatus] = useState('');
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(true);

  const loadAllSessions = useCallback(async (headers) => {
    const url = `${process.env.REACT_APP_API_URL}/getallcoaching/${id}`;
    try {
      const res = await axios.get(url, { headers });
      setSessions(res.data);
    } catch (err) {
      console.log("Error loading coaching sessions:", err);
    }
  }, [id]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    loadAllSessions(headers);
  }, [id, loadAllSessions, editId]);

  useEffect(() => {
    if (sessions.length === 0) {
      setIsFormOpen(true);
    } else {
      setIsFormOpen(false);
    }
  }, [sessions.length]);

  const validateFields = () => {
    const requiredFields = {
      'Start Date': coachingData.start_date,
      'Session Number': coachingData.session_number,
      'Action Recommended': coachingData.action_recommended,
      'Venue': coachingData.venue,
      'Discussion': coachingData.discussion
    };

    const emptyFields = Object.entries(requiredFields)
      .filter(([_, value]) => !value)
      .map(([fieldName, _]) => fieldName);

    if (emptyFields.length > 0) {
      setError(`Please fill in all required fields: ${emptyFields.join(', ')}`);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateFields()) return;

    const token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const url = `${process.env.REACT_APP_API_URL}/savecoaching/${id}`;
    setStatus("saving...");
    setError("");

    try {
      await axios.post(url, coachingData, { headers });
      setStatus("saved");
      setError("");
      await loadAllSessions(headers);
      
      setCoachingData({
        start_date: '',
        session_number: '1',
        action_recommended: '',
        venue: '',
        discussion: ''
      });
    } catch (err) {
      console.log(err);
      setError(err.response?.data?.error || "error saving data");
      setStatus("");
    }
  };

  const handleUpdate = async () => {
    if (!validateFields()) return;

    const token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const editUrl = `${process.env.REACT_APP_API_URL}/editcoaching/${id}/${editId}`;
    try {
      await axios.put(editUrl, coachingData, { headers });
      
      setCoachingData({
        start_date: '',
        session_number: '1',
        action_recommended: '',
        venue: '',
        discussion: ''
      });
      
      setEditId(null);
      await loadAllSessions(headers);
    } catch (err) {
      console.log("Error editing coaching session:", err);
      setError("Error editing coaching session");
    }
  };

  const handleDelete = async (sessionId) => {
    const token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const url = `${process.env.REACT_APP_API_URL}/deletecoaching/${sessionId}`;

    try {
      await axios.delete(url, { headers });
      loadAllSessions(headers);
    } catch (err) {
      console.log("Error deleting coaching session:", err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <div className="container">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-end mb-3">
            <Button 
              onClick={() => setIsFormOpen(!isFormOpen)}
              variant="outline-primary"
              size="sm"
            >
              {isFormOpen ? 'Hide Form' : 'Add Coaching Session'}
            </Button>
          </div>

          <Collapse in={isFormOpen}>
            <div>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={coachingData.start_date}
                      onChange={(e) =>
                        setCoachingData({ ...coachingData, start_date: e.target.value })
                      }
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Session Number</Form.Label>
                    <Form.Select
                      value={coachingData.session_number}
                      onChange={(e) =>
                        setCoachingData({ ...coachingData, session_number: e.target.value })
                      }
                      required
                    >
                      {[1, 2, 3, 4, 5].map(num => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>

                <div className="col-md-12">
                  <Form.Group className="mb-3">
                    <Form.Label>Action Recommended</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={coachingData.action_recommended}
                      onChange={(e) =>
                        setCoachingData({ ...coachingData, action_recommended: e.target.value })
                      }
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-12">
                  <Form.Group className="mb-3">
                    <Form.Label>Venue</Form.Label>
                    <Form.Control
                      type="text"
                      value={coachingData.venue}
                      onChange={(e) =>
                        setCoachingData({ ...coachingData, venue: e.target.value })
                      }
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-12">
                  <Form.Group className="mb-3">
                    <Form.Label>Discussion</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      value={coachingData.discussion}
                      onChange={(e) =>
                        setCoachingData({ ...coachingData, discussion: e.target.value })
                      }
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-12">
                  {editId ? 
                    <Button onClick={handleUpdate} style={{ marginTop: "20px" }}>Update</Button> : 
                    <Button onClick={handleSave} style={{ marginTop: "20px" }}>Save</Button>
                  }
                  <div className="status">{status}</div>
                  <div className="error" style={{ color: 'red' }}>{error}</div>
                </div>
              </div>
            </div>
          </Collapse>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <h2>All Coaching Sessions</h2>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Date</th>
                <th>Session</th>
                <th>Action Recommended</th>
                <th>Venue</th>
                <th>Discussion</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <tr key={session.id}>
                  <td>{formatDate(session.start_date)}</td>
                  <td>{session.session_number}</td>
                  <td>{session.action_recommended}</td>
                  <td>{session.venue}</td>
                  <td>{session.discussion}</td>
                  <td>
                    <Button className="btn btn-outline-primary btn-sm me-2" onClick={() => {
                      setEditId(session.id);
                      setIsFormOpen(true);
                      setCoachingData({
                        start_date: session.start_date || '',
                        session_number: session.session_number || '1',
                        action_recommended: session.action_recommended || '',
                        venue: session.venue || '',
                        discussion: session.discussion || ''
                      });
                    }}>
                      Edit
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => handleDelete(session.id)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
}
